/**
 * Informações e limites da leitura (E139.F3, GUS-142, ADR-0095 §2.7), na pré-análise eletrônica.
 *
 * Cada informação do caso com o valor usado no exame, o texto impresso, a situação e a fonte; a
 * divergência na linha; os documentos do caso com cada página; e "ver na fonte", que abre a página
 * do arquivo recebido. A ficha diz o que a leitura achou e o que não achou: a ausência no
 * documento só o perito afirma, e informação extraída não é prova automática de autoria.
 *
 * E155.F3 (GUS-192, ADR-0105 §2.7): "Rever leitura de um campo" abre o cartão "Rever leitura na
 * fonte" na linha; o ponto do perito da conferência pede o mesmo cartão por `reverLeitura`; e
 * "Rever documento na fonte" relê de uma vez as informações esperadas num documento. A releitura
 * registrada relê a ficha; a que mudou o exame avisa a página (`onMudou`).
 */
import type { AxiosInstance } from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  temPaginaEmLeitura,
  type CampoDaFichaData,
  type CampoDaFichaId,
  type FichaDaLeituraData,
  type FichaDaLeituraOutData,
  type PaginaNaFonte,
} from '@/lib/ficha_da_leitura';
import { chaveDaReleitura, type ResultadoDaReleitura } from '@/lib/releitura_da_leitura';
import { formatarDataHora } from '@/lib/trilha_format';
import { useFichaDaLeitura, type EstadoDaFicha } from '@/lib/useFichaDaLeitura';

import { AncoraDaFicha } from './AncoraDaFicha';
import { CamposDaFicha } from './CamposDaFicha';
import { DocumentosDaFicha } from './DocumentosDaFicha';
import { PaginaDaFonte } from './PaginaDaFonte';
import { DocumentosParaRever } from './ReverDocumento';
import { ReverLeitura } from './ReverLeitura';
import { preencher, t } from './textos';

const tm = ptBR.mesaUnica.ficha;

/** O pedido de abrir o cartão num campo (o ponto do perito); `vez` muda a cada pedido. */
export interface PedidoDeReverLeitura {
  campo: CampoDaFichaId;
  referenteId: string | null;
  vez: number;
}

interface PedidosDaFicha {
  /** E205.F4.C02: só consulta — valores, fontes e páginas, sem "Rever leitura". */
  consulta: boolean;
  client: AxiosInstance;
  caseId: string;
  aberto: string | null;
  onAbrir: (chave: string) => void;
  onFechar: () => void;
  onVerNaFonte: (alvo: PaginaNaFonte) => void;
  onFeito: (resultado: ResultadoDaReleitura) => void;
}

function Pronta({ dados, pedidos }: { dados: FichaDaLeituraOutData; pedidos: PedidosDaFicha }) {
  const { ficha: registro } = dados;
  const { aberto } = pedidos;
  const campoAberto = registro.ficha.campos.find(
    (campo) => chaveDaReleitura(campo.campo, campo.referente_id) === aberto,
  );
  const cartao =
    campoAberto === undefined || pedidos.consulta ? null : (
      <ReverLeitura
        key={aberto}
        client={pedidos.client}
        caseId={pedidos.caseId}
        campo={campoAberto}
        documentos={registro.ficha.documentos}
        versao={registro.versao}
        onVerNaFonte={pedidos.onVerNaFonte}
        onFeito={pedidos.onFeito}
        onFechar={pedidos.onFechar}
      />
    );
  return (
    <>
      <p className="text-body-sm text-fg-muted" data-testid="ficha-versao">
        {preencher(t.versao, {
          quando: formatarDataHora(registro.criada_em),
          versao: registro.versao,
        })}
      </p>
      {temPaginaEmLeitura(registro.ficha) ? (
        <p role="status" className="text-body-sm text-fg" data-testid="ficha-em-leitura">
          {t.emLeitura}
        </p>
      ) : null}
      <CamposDaFicha
        ficha={registro.ficha}
        onVerNaFonte={pedidos.onVerNaFonte}
        aberto={aberto}
        onReverLeitura={
          pedidos.consulta
            ? undefined
            : (campo: CampoDaFichaData) =>
                pedidos.onAbrir(chaveDaReleitura(campo.campo, campo.referente_id))
        }
        cartao={cartao}
      />
      <DocumentosDaFicha paginas={registro.ficha.paginas} onVerNaFonte={pedidos.onVerNaFonte} />
      {pedidos.consulta ? null : (
        <DocumentosParaRever
          client={pedidos.client}
          caseId={pedidos.caseId}
          documentos={registro.ficha.documentos}
          onFeito={pedidos.onFeito}
        />
      )}
    </>
  );
}

function Conteudo({
  ficha,
  onReler,
  pedidos,
}: {
  ficha: EstadoDaFicha;
  onReler: () => void;
  pedidos: PedidosDaFicha;
}): JSX.Element {
  if (ficha.estado === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  }
  if (ficha.estado === 'vazio') {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="ficha-vazia">
        {t.vazio}
      </p>
    );
  }
  if (ficha.estado === 'erro') {
    return (
      <div role="alert" className="space-y-sc-2" data-testid="ficha-erro">
        <p className="text-body-sm text-danger-700">
          {ficha.motivo === 'sem_permissao' ? t.semPermissao : t.erro}
        </p>
        {ficha.motivo === 'indisponivel' ? (
          <Button type="button" variant="secondary" size="sm" onClick={onReler}>
            {t.tentarDeNovo}
          </Button>
        ) : null}
      </div>
    );
  }
  return <Pronta dados={ficha.dados} pedidos={pedidos} />;
}

export interface FichaDaLeituraProps {
  client: AxiosInstance;
  caseId: string;
  /** Muda quando o caso é relido (exame, documento novo): a ficha é lida de novo. */
  releitura?: unknown;
  /** Abre o cartão "Rever leitura na fonte" no campo pedido (o ponto do perito, E155.F3). */
  reverLeitura?: PedidoDeReverLeitura | null;
  /** A releitura mudou o exame: a página relê o caso. */
  onMudou?: (() => void) | undefined;
  /**
   * E203.F5.T05 (ADR-0146): na mesa a ficha vem recolhida, com o resumo "N informações · M
   * divergências"; abre sozinha com divergência, com o ponto do perito ("Rever leitura") ou com a
   * âncora `#ficha-da-leitura` no endereço (E203.F5.C).
   */
  recolhida?: boolean;
  /**
   * E205.F4.C02 (F-E205.F4-02): só consulta, em "Informações da leitura" (o estudo: "mesma tela, só
   * leitura, para auditar"): valores, fontes e páginas, sem "Rever leitura" nem "Rever documento".
   */
  consulta?: boolean;
}

/** Quantas informações a ficha tem e quantas divergem entre as fontes. */
export function resumoDaFicha(ficha: FichaDaLeituraData): {
  informacoes: number;
  divergencias: number;
} {
  return {
    informacoes: ficha.campos.length,
    divergencias: ficha.campos.filter((c) => c.divergencias.length > 0).length,
  };
}

/** Recolhida, abre pela escolha do perito ou sozinha com divergência (ou sem a ficha pronta). */
function fichaVisivel(
  recolhida: boolean,
  expandida: boolean | null,
  resumo: ReturnType<typeof resumoDaFicha> | null,
): boolean {
  if (!recolhida) return true;
  return expandida ?? (resumo === null || resumo.divergencias > 0);
}

function textoDoResumo({ informacoes, divergencias }: ReturnType<typeof resumoDaFicha>): string {
  const n = (informacoes === 1 ? tm.umaInformacao : tm.informacoes).replace(
    '{n}',
    String(informacoes),
  );
  const m = (divergencias === 1 ? tm.umaDivergencia : tm.divergencias).replace(
    '{m}',
    String(divergencias),
  );
  return `${n} · ${m}`;
}

export function FichaDaLeitura({
  client,
  caseId,
  releitura,
  reverLeitura = null,
  onMudou,
  recolhida = false,
  consulta = false,
}: FichaDaLeituraProps): JSX.Element {
  const { ficha, reler } = useFichaDaLeitura(client, caseId, releitura);
  const [alvo, setAlvo] = useState<PaginaNaFonte | null>(null);
  const [aberto, setAberto] = useState<string | null>(null);
  // `null` até o perito abrir ou recolher: aí decide a divergência (ou a ficha que não está pronta).
  const [expandida, setExpandida] = useState<boolean | null>(null);
  const abrirPelaAncora = useCallback(() => setExpandida(true), []);

  useEffect(() => {
    if (reverLeitura === null) return;
    setExpandida(true);
    setAberto(chaveDaReleitura(reverLeitura.campo, reverLeitura.referenteId));
  }, [reverLeitura]);

  useEffect(() => {
    if (aberto === null) return;
    const cartao = document.querySelector('[data-testid="releitura-cartao"]');
    cartao?.scrollIntoView({ block: 'center' });
  }, [aberto, ficha]);

  const onFeito = useCallback(
    (resultado: ResultadoDaReleitura) => {
      reler();
      if (resultado === 'mudou') onMudou?.();
    },
    [reler, onMudou],
  );
  const pedidos: PedidosDaFicha = {
    consulta,
    client,
    caseId,
    aberto,
    onAbrir: (chave) => setAberto((anterior) => (anterior === chave ? null : chave)),
    onFechar: () => setAberto(null),
    onVerNaFonte: setAlvo,
    onFeito,
  };
  const resumo = ficha.estado === 'pronto' ? resumoDaFicha(ficha.dados.ficha.ficha) : null;
  const visivel = fichaVisivel(recolhida, expandida, resumo);
  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-4 p-sc-6"
      data-testid="ficha-da-leitura"
      data-estado={ficha.estado}
      data-recolhida={!visivel}
      aria-labelledby="ficha-da-leitura-titulo"
    >
      <div className="flex flex-wrap items-start justify-between gap-sc-3">
        <div>
          <h3 id="ficha-da-leitura-titulo" className="text-body-lg text-fg">
            {t.titulo}
          </h3>
          {recolhida && resumo !== null ? (
            <p className="mt-1 text-body-sm text-fg-muted" data-testid="ficha-resumo">
              {textoDoResumo(resumo)}
            </p>
          ) : null}
          {visivel ? (
            <p className="mt-1 text-body-sm font-semibold text-fg" data-testid="ficha-ressalva">
              {t.ressalva}
            </p>
          ) : null}
        </div>
        {recolhida ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={visivel}
            aria-controls="ficha-da-leitura-corpo"
            onClick={() => setExpandida(!visivel)}
            data-testid="ficha-alternar"
          >
            {visivel ? tm.recolher : tm.abrir}
          </Button>
        ) : null}
      </div>
      {visivel ? (
        <div id="ficha-da-leitura-corpo" className="space-y-sc-4">
          <Conteudo ficha={ficha} onReler={reler} pedidos={pedidos} />
        </div>
      ) : null}
      <PaginaDaFonte client={client} caseId={caseId} alvo={alvo} onFechar={() => setAlvo(null)} />
      {recolhida ? <AncoraDaFicha onPedido={abrirPelaAncora} /> : null}
    </Blueprint>
  );
}
